import type { Ssm5ConnectPhase } from '@packages/domain/ssm5';

import { useTranslation } from 'react-i18next';

import { Ssm5LoaderBar } from './ssm5-loader-bar';

/** How far the loader bar is per connect phase outside a VIN session. */
const PHASE_PROGRESS_PERCENT: Record<Ssm5ConnectPhase, number> = {
    identifyingVehicle: 70,
    matchingVehicle: 90,
    pairing: 25,
    readingVin: 50,
};

/** The message under the loader bar inside a VIN session, per connect phase. */
const IN_SESSION_MESSAGE: Record<Ssm5ConnectPhase, string> = {
    identifyingVehicle: 'ssm5:connection.readingCodes',
    matchingVehicle: 'ssm5:connection.loading',
    pairing: 'ssm5:connection.readingCodes',
    readingVin: 'ssm5:connection.readingCodes',
};

/** Props for {@link Ssm5ConnectingPanel}. */
export type Ssm5ConnectingPanelProps = {
    /** Whether the connect flow runs inside an open VIN session. */
    isInVinSession: boolean;
    /** The phase the connect attempt is in. */
    phase: Ssm5ConnectPhase;
    /** How far the attempt is, from 0 to 100, as the service reports it. */
    progress: number;
};

/**
 * Full-body loading state of the connect flow: a centered loader bar over the matching message, moving by
 * phase. Inside a VIN session, where the vehicle is already known, the bar follows the progress the service
 * reports over the reading codes message, then starts over for the matching message.
 *
 * @param props - {@link Ssm5ConnectingPanelProps}
 * @returns the connecting panel
 */
export function Ssm5ConnectingPanel({ isInVinSession, phase, progress }: Ssm5ConnectingPanelProps) {
    const { t } = useTranslation();
    const percent = isInVinSession ? progress : PHASE_PROGRESS_PERCENT[phase];
    const message = isInVinSession ? IN_SESSION_MESSAGE[phase] : 'ssm5:connection.loading';

    return <Ssm5LoaderBar message={t(message)} percent={percent} />;
}
